import * as Phaser from "phaser-ce"
import { COLOR } from "../../../common/Color"
import { Align, FontType } from "../../../common/TextUtils"
import { StudentStoreSA } from "../../../react/students/StudentStoreSA"
import { ActivityKey } from "../../../shared/models/ActivityKey"
import { BackgroundSprite } from "../../generic-sprites/BackgroundSprite"
import { Buttons, ButtonType } from "../../generic-sprites/Buttons"
import { RadioGroup, RadioOption } from "../../generic-sprites/RadioButtons"
import { Rectangle } from "../../generic-sprites/Shape"
import { SpeechBubble } from "../../generic-sprites/SpeechBubble"
import { colorNum } from "../../utils/ColorUtils"
import { PhaserText } from "../../utils/PhaserText"
import { Scale, x, y } from "../../utils/Scale"
import { Sprites } from "../../utils/Sprites"
import { MakeFriendAssets, MakeFriendAudio, MakeFriendImages } from "./MakeFriendAssets"
import { MakeFriendProgressStore, MakeFriendStages } from "./MakeFriendProgressStore"
import { StageTypes } from "./MakeFriendTypes"
import { CharacterContext, CharacterType, MakeFriendCharacter } from "./sprites/MakeFriendCharacter"

enum ProgressionStepTypes {
    BACKGROUND ="background",
    CHARACTER = "character",
    FOREGROUND_IMAGE = "foregroundimage",
    NAME_TAG = "nametag",
    YOU_SPEECH = "youspeech",
    FRIEND_SPEECH = "friendspeech",
    SMOKER_SPEECH = "smokerspeech",
    NARRATION = "narration",
    MODAL = "modal",
    NEXT_BUTTON = "nextbutton",
    FADE_SCREEN = "fadescreen"
}

export interface ShowBackground {
    image?: string
    imagePrefix?: string
}

export interface Character {
    type: CharacterType
    context: CharacterContext
    xPosition?: number
    girlXPosition?: number
    boyXPosition?: number
    yPosition?: number
    girlYPosition?: number
    boyYPosition?: number
    scale: number
}

export interface ForegroundImage {
    image?: string
    boyImage?: string
    girlImage?: string
    xPosition: number
    yPosition: number
    scale: number
}

export interface NameTag {
    type: CharacterType
    xPosition?: number
    girlXPosition?: number
    boyXPosition?: number
    yPosition?: number
    girlYPosition?: number
    boyYPosition?: number
}

interface SpeechAudioChoice {
    stage: MakeFriendStages
    number: number
}

export interface NPCSpeech {
    audio: SpeechAudioChoice
    text?: string
    girlText?: string
    boyText?: string
    xPosition?: number
    girlXPosition?: number
    boyXPosition?: number
    yPosition?: number
    girlYPosition?: number
    boyYPosition?: number
    width?: number
    girlWidth?: number
    boyWidth?: number
    arrowLeft?: boolean
    girlArrowLeft?: boolean
    boyArrowLeft?: boolean
}

export interface YouSpeech {
    audioChoices: Array<SpeechAudioChoice>
    xPosition?: number
    girlXPosition?: number
    boyXPosition?: number
    yPosition?: number
    girlYPosition?: number
    boyYPosition?: number
    width?: number
}

export interface Narration {
    audio: string
}

export interface Modal {
    choices?: Array<RadioOption>
    girlChoices?: Array<RadioOption>
    boyChoices?: Array<RadioOption>
}

export interface NextButton {}

export interface FadeScreen {
    duration: number
    text?: string
    unfade?: boolean
}

export interface ProgressionStep {
    type: ProgressionStepTypes
    step: ShowBackground | Character | ForegroundImage | NameTag | NPCSpeech | Modal | YouSpeech | Narration | NextButton | FadeScreen
}

export interface Scene {
    setupSteps: Array<ProgressionStep>
    sceneSteps: Array<ProgressionStep>
}

export interface StageProgression {
    type: MakeFriendStages,
    scenes: Array<Scene>
}

export const MakeFriendStageProgressions: Array<StageProgression> = [
    {
        type: MakeFriendStages.ARCADE,
        scenes: [
            {
                setupSteps: [
                    {type: ProgressionStepTypes.BACKGROUND, step: {imagePrefix: 'ARCADE_EXTERIOR_'}},
                    {type: ProgressionStepTypes.CHARACTER, step: {type: CharacterType.YOU, context: CharacterContext.ARCADE_OUTSIDE, xPosition: x(.366), yPosition: y(.404), scale: 1}},
                    {type: ProgressionStepTypes.NAME_TAG, step: {type: CharacterType.YOU, xPosition: x(.427), yPosition: y(.739)}},
                    {type: ProgressionStepTypes.CHARACTER, step: {type: CharacterType.FRIEND, context: CharacterContext.ARCADE_OUTSIDE, xPosition: x(.173), yPosition: y(.421), scale: 1}},
                    {type: ProgressionStepTypes.NAME_TAG, step: {type: CharacterType.FRIEND, xPosition: x(.284), yPosition: y(.823)}}

                ],
                sceneSteps: [
                    {type: ProgressionStepTypes.FRIEND_SPEECH, step: {audio: {stage: MakeFriendStages.ARCADE, number: 1}, text: "Ew, some of those guys are vaping. That’s so uncool.", xPosition: x(.06), yPosition: y(.25)}},
                    {type: ProgressionStepTypes.MODAL, step: {choices: [{text: "I know. I can’t believe they are vaping. Yuck!", wordWrapWidth: .5}, {text: "You’re right…I don’t think kids who vape are cool at all!", wordWrapWidth: .5}, {text: "Yeah, I don’t want to hang around them. They just aren't cool. Let’s go.", wordWrapWidth: .5}]}},
                    {type: ProgressionStepTypes.YOU_SPEECH, step: {audioChoices: [{stage: MakeFriendStages.ARCADE, number: 1}, {stage: MakeFriendStages.ARCADE, number: 2}, {stage: MakeFriendStages.ARCADE, number: 3}], xPosition: x(.2), yPosition: y(.18)}},
                    {type: ProgressionStepTypes.SMOKER_SPEECH, step: {audio: {stage: MakeFriendStages.ARCADE, number: 1}, text: "Hey guys, whaddya doin'?", xPosition: x(.7), yPosition: y(.4)}},
                    {type: ProgressionStepTypes.FRIEND_SPEECH, step: {audio: {stage: MakeFriendStages.ARCADE, number: 2}, text: "We’re goin’ to the arcade.", xPosition: x(.06), yPosition: y(.3)}},
                    {type: ProgressionStepTypes.SMOKER_SPEECH, step: {audio: {stage: MakeFriendStages.ARCADE, number: 2}, text: "Wanna hang out with us for awhile?", xPosition: x(.7), yPosition: y(.4)}},
                    {type: ProgressionStepTypes.FRIEND_SPEECH, step: {audio: {stage: MakeFriendStages.ARCADE, number: 3}, text: "No thanks. Wanna go to the arcade with us instead?", xPosition: x(.06), yPosition: y(.25)}},
                    {type: ProgressionStepTypes.SMOKER_SPEECH, step: {audio: {stage: MakeFriendStages.ARCADE, number: 3}, text: "No, we’re gonna hang out here.", xPosition: x(.7), yPosition: y(.4)}},
                ]
            },
            {
                setupSteps: [
                    {type: ProgressionStepTypes.BACKGROUND, step: {imagePrefix: "ARCADE_INTERIOR_"}},
                    {type: ProgressionStepTypes.CHARACTER, step: {type: CharacterType.YOU, context: CharacterContext.ARCADE_INSIDE, xPosition: x(.507), yPosition: y(.367), scale: 1}},
                    {type: ProgressionStepTypes.NAME_TAG, step: {type: CharacterType.YOU, xPosition: x(.581), yPosition: y(.819)}},
                    {type: ProgressionStepTypes.CHARACTER, step: {type: CharacterType.FRIEND, context: CharacterContext.ARCADE_INSIDE, xPosition: x(.294), yPosition: y(.339), scale: 1}},
                    {type: ProgressionStepTypes.NAME_TAG, step: {type: CharacterType.FRIEND, xPosition: x(.379), yPosition: y(.687)}}

                ],
                sceneSteps: [
                    {type: ProgressionStepTypes.FRIEND_SPEECH, step: {audio: {stage: MakeFriendStages.ARCADE, number: 4}, text: "I’m glad those vapers are out there. I wouldn’t want to be seen with them. They just aren’t cool.", xPosition: x(.14), yPosition: y(.05)}},
                    {type: ProgressionStepTypes.MODAL, step: {choices: [{text: "I wouldn’t want to be seen with them either. I think vapers are gross.", wordWrapWidth: .5}, {text: "Yeah, playing video games is really cool, but I think vapers are definitely not.", wordWrapWidth: .5}, {text: "I know. I’d be embarrassed to be seen with someone who’s vaping. They just aren’t cool.", wordWrapWidth: .5}]}},
                    {type: ProgressionStepTypes.YOU_SPEECH, step: {audioChoices: [{stage: MakeFriendStages.ARCADE, number: 4}, {stage: MakeFriendStages.ARCADE, number: 5}, {stage: MakeFriendStages.ARCADE, number: 6}], xPosition: x(.32), yPosition: y(.12)}},
                    {type: ProgressionStepTypes.FRIEND_SPEECH, step: {audio: {stage: MakeFriendStages.ARCADE, number: 5}, text: "Let’s go play some games!", xPosition: x(.14), yPosition: y(.25)}},
                    {type: ProgressionStepTypes.NARRATION, step: {audio: MakeFriendAudio.ARCADE_END}},
                    {type: ProgressionStepTypes.NEXT_BUTTON, step: {}},
                ]
            }
        ]
    },
    {
        type: MakeFriendStages.SLEEPOVER,
        scenes: [
            {
                setupSteps: [
                    {type: ProgressionStepTypes.BACKGROUND, step: {imagePrefix: "SLEEPOVER_ROOM_"}},
                    {type: ProgressionStepTypes.CHARACTER, step: {type: CharacterType.YOU, context: CharacterContext.SLEEPOVER_BEDROOM, girlXPosition: x(0), girlYPosition: y(.7), boyXPosition: x(.533), boyYPosition: y(.339), scale: 1}},
                    {type: ProgressionStepTypes.NAME_TAG, step: {type: CharacterType.YOU, girlXPosition: x(.086), girlYPosition: y(.872), boyXPosition: x(.842), boyYPosition: y(.749)}},
                    {type: ProgressionStepTypes.CHARACTER, step: {type: CharacterType.FRIEND, context: CharacterContext.SLEEPOVER_BEDROOM, girlXPosition: x(.629), girlYPosition: y(.461), boyXPosition: x(.089), boyYPosition: y(.428), scale: 1}},
                    {type: ProgressionStepTypes.NAME_TAG, step: {type: CharacterType.FRIEND, girlXPosition: x(.82), girlYPosition: y(.824), boyXPosition: x(.254), boyYPosition: y(.752)}}
                ],
                sceneSteps: [
                    {type: ProgressionStepTypes.SMOKER_SPEECH, step: {audio: {stage: MakeFriendStages.SLEEPOVER, number: 1}, text: "Did you hear about the party at Chris’ house next weekend?", girlXPosition: x(.08), girlYPosition: y(.1), boyXPosition: x(.31), boyYPosition: y(.02), boyWidth: 0.26}},
                    {type: ProgressionStepTypes.FRIEND_SPEECH, step: {audio: {stage: MakeFriendStages.SLEEPOVER, number: 1}, text: "Yeah. I think we’re gonna go, are you?", girlXPosition: x(.62), girlYPosition: y(.37), boyXPosition: x(.24), boyYPosition: y(.32), boyArrowLeft: true}},
                    {type: ProgressionStepTypes.SMOKER_SPEECH, step: {audio: {stage: MakeFriendStages.SLEEPOVER, number: 2}, girlText: "Yeah, I can’t wait! I even took some cigarettes from my uncle so I can hang out with Jen and her friends.", boyText: "Yeah, I can’t wait! I even took some cigarettes from my uncle so I can hang out with Jake and his friends.", girlXPosition: x(.08), girlYPosition: y(.02), boyXPosition: x(.14), boyYPosition: y(.02), boyWidth: 0.43}},
                    {type: ProgressionStepTypes.FRIEND_SPEECH, step: {audio: {stage: MakeFriendStages.SLEEPOVER, number: 2}, girlText: "Why would you wanna do that? Nobody wants to hang out with Jen.", boyText: "Why would you wanna do that? Nobody wants to hang out with Jake.", girlXPosition: x(.62), girlYPosition: y(.32), boyXPosition: x(.24), boyYPosition: y(.25), boyArrowLeft: true}},
                    {type: ProgressionStepTypes.MODAL, step: {
                        girlChoices: [{text: "Yeah, I think being around Jen and those smokers wouldn't be any fun.", wordWrapWidth: .5}, {text: "I really don’t want you to hang out with Jen and those smokers, they’re so unpopular.", wordWrapWidth: .5}, {text: "I don’t want you to hang out with smokers. I want you to hang out with all our friends… they’re much more fun.", wordWrapWidth: .5}],
                        boyChoices: [{text: "Yeah, I think being around Jake and those smokers wouldn't be any fun.", wordWrapWidth: .5}, {text: "I really don’t want you to hang out with Jake and those smokers, they’re so unpopular.", wordWrapWidth: .5}, {text: "I don’t want you to hang out with smokers. I want you to hang out with all our friends… they’re much more fun.", wordWrapWidth: .5}]
                    }},
                    {type: ProgressionStepTypes.YOU_SPEECH, step: {audioChoices: [{stage: MakeFriendStages.SLEEPOVER, number: 1}, {stage: MakeFriendStages.SLEEPOVER, number: 2}, {stage: MakeFriendStages.SLEEPOVER, number: 3}], girlXPosition: x(.01), girlYPosition: y(.42), boyXPosition: x(.62), boyYPosition: y(.15)}},
                    {type: ProgressionStepTypes.FADE_SCREEN, step: {duration: 4000, text: "Later next week…"}}
                ]
            },
            {
                setupSteps: [
                    {type: ProgressionStepTypes.BACKGROUND, step: {imagePrefix: 'SLEEPOVER_PARTY_OUTSIDE_'}},
                    {type: ProgressionStepTypes.CHARACTER, step: {type: CharacterType.YOU, context: CharacterContext.SLEEPOVER_OUTSIDE, xPosition: x(.197), yPosition: y(.452), scale: 1}},
                    {type: ProgressionStepTypes.NAME_TAG, step: {type: CharacterType.YOU, xPosition: x(.263), yPosition: y(.807)}},
                    {type: ProgressionStepTypes.CHARACTER, step: {type: CharacterType.FRIEND, context: CharacterContext.SLEEPOVER_OUTSIDE, xPosition: x(.284), yPosition: y(.422), scale: 1}},
                    {type: ProgressionStepTypes.NAME_TAG, step: {type: CharacterType.FRIEND, xPosition: x(.393), yPosition: y(.865)}},
                    {type: ProgressionStepTypes.FOREGROUND_IMAGE, step: {boyImage: 'SLEEPOVER_PARTY_OUTSIDE_SMOKER_BOY', girlImage: 'SLEEPOVER_PARTY_OUTSIDE_SMOKER_GIRL', xPosition: x(0.077), yPosition: y(.451), scale: 1}},
                ],
                sceneSteps: [
                    {type: ProgressionStepTypes.FADE_SCREEN, step: {duration: 4000, text: "Later next week…", unfade: true}},
                    {type: ProgressionStepTypes.SMOKER_SPEECH, step: {audio: {stage: MakeFriendStages.SLEEPOVER, number: 3}, text: "I’m gonna go over there and smoke. Wanna come?", xPosition: x(.2), yPosition: y(.3), arrowLeft: true}},
                    {type: ProgressionStepTypes.FRIEND_SPEECH, step: {audio: {stage: MakeFriendStages.SLEEPOVER, number: 3}, text: "Nah, we wanna go inside and check out the party!", xPosition: x(.44), yPosition: y(.33), arrowLeft: true}},
                ]
            },
            {
                setupSteps: [
                    {type: ProgressionStepTypes.BACKGROUND, step: {imagePrefix: 'SLEEPOVER_PARTY_INSIDE_'}},
                    {type: ProgressionStepTypes.CHARACTER, step: {type: CharacterType.YOU, context: CharacterContext.SLEEPOVER_INSIDE, xPosition: x(.205), yPosition: y(.321), scale: 1}},
                    {type: ProgressionStepTypes.NAME_TAG, step: {type: CharacterType.YOU, xPosition: x(.278), yPosition: y(.778)}},
                    {type: ProgressionStepTypes.CHARACTER, step: {type: CharacterType.FRIEND, context: CharacterContext.SLEEPOVER_INSIDE, xPosition: x(.003), yPosition: y(.332), scale: 1}},
                    {type: ProgressionStepTypes.NAME_TAG, step: {type: CharacterType.FRIEND, xPosition: x(.079), yPosition: y(.736)}},
                    {type: ProgressionStepTypes.FOREGROUND_IMAGE, step: {image: 'SLEEPOVER_PARTY_INSIDE_FOREGROUND', xPosition: x(0), yPosition: y(.753), scale: 1}},
                ],
                sceneSteps: [
                    {type: ProgressionStepTypes.FRIEND_SPEECH, step: {audio: {stage: MakeFriendStages.SLEEPOVER, number: 4}, text: "This party looks awesome! I’m really glad we’re not outside smoking; they don’t look like they’re having any fun.", xPosition: x(.13), yPosition: y(.22), width: .35, arrowLeft: true}},
                    {type: ProgressionStepTypes.MODAL, step: {choices: [{text: "I like being in here with all our cool friends.", wordWrapWidth: .5}, {text: "Yeah, I think you’re right. Nobody wants to hang out with them because they’re smoking.", wordWrapWidth: .5}, {text: "I feel really bad for them, out there all by themselves.", wordWrapWidth: .5}]}},
                    {type: ProgressionStepTypes.YOU_SPEECH, step: {audioChoices: [{stage: MakeFriendStages.SLEEPOVER, number: 4}, {stage: MakeFriendStages.SLEEPOVER, number: 5}, {stage: MakeFriendStages.SLEEPOVER, number: 6}], xPosition: x(.33), yPosition: y(.23), width: .4, arrowLeft: true}},
                    {type: ProgressionStepTypes.FRIEND_SPEECH, step: {audio: {stage: MakeFriendStages.SLEEPOVER, number: 5}, text: "Yeah. It’s kind of dumb to be outside smoking in the rain.", xPosition: x(.12), yPosition: y(.13), arrowLeft: true}},
                    {type: ProgressionStepTypes.NARRATION, step: {audio: MakeFriendAudio.PARTY_END}},
                    {type: ProgressionStepTypes.NEXT_BUTTON, step: {}},
                ]
            },
        ]
    },
    {
        type: MakeFriendStages.POOL,
        scenes: [
            {
                setupSteps: [
                    {type: ProgressionStepTypes.BACKGROUND, step: {imagePrefix: 'POOL_IN_BLUE_'}},
                    {type: ProgressionStepTypes.CHARACTER, step: {type: CharacterType.YOU, context: CharacterContext.POOL_IN, xPosition: x(.519), yPosition: y(.564), scale: 1}},
                    {type: ProgressionStepTypes.NAME_TAG, step: {type: CharacterType.YOU, xPosition: x(.613), yPosition: y(.825)}},
                    {type: ProgressionStepTypes.CHARACTER, step: {type: CharacterType.FRIEND, context: CharacterContext.POOL_IN, xPosition: x(.009), yPosition: y(.476), scale: 1}},
                    {type: ProgressionStepTypes.NAME_TAG, step: {type: CharacterType.FRIEND, xPosition: x(.207), yPosition: y(.769)}}
                ],
                sceneSteps: [
                    {type: ProgressionStepTypes.FRIEND_SPEECH, step: {audio: {stage: MakeFriendStages.POOL, number: 1}, girlText: "Hey look over there. It’s Meg vaping with her friends! Why would she be doing that?", boyText: "Hey look over there. It’s Mark vaping with his friends! Why would he be doing that?", xPosition: x(.24), yPosition: y(.26), arrowLeft: true}},
                    {type: ProgressionStepTypes.MODAL, step: {choices: [{text: "I don’t know… I don’t think vaping looks like much fun.", wordWrapWidth: .5}, {text: "Who knows… I think those vapers look really bored.", wordWrapWidth: .5}, {text: "Hmmm… those vapers look really unhappy.", wordWrapWidth: .5}]}},
                    {type: ProgressionStepTypes.YOU_SPEECH, step: {audioChoices: [{stage: MakeFriendStages.POOL, number: 1}, {stage: MakeFriendStages.POOL, number: 2}, {stage: MakeFriendStages.POOL, number: 3}], xPosition: x(.35), yPosition: y(.4)}},
                ]
            },
            {
                setupSteps: [
                    {type: ProgressionStepTypes.BACKGROUND, step: {image: 'POOL_OUT_BACKGROUND'}},
                    {type: ProgressionStepTypes.CHARACTER, step: {type: CharacterType.YOU, context: CharacterContext.POOL_OUT, xPosition: x(.831), yPosition: y(.413), scale: 1}},
                    {type: ProgressionStepTypes.NAME_TAG, step: {type: CharacterType.YOU, xPosition: x(.898), yPosition: y(.608)}},
                    {type: ProgressionStepTypes.CHARACTER, step: {type: CharacterType.FRIEND, context: CharacterContext.POOL_OUT, xPosition: x(.547), yPosition: y(.257), scale: 1}},
                    {type: ProgressionStepTypes.NAME_TAG, step: {type: CharacterType.FRIEND, xPosition: x(.682), yPosition: y(.581)}},
                    {type: ProgressionStepTypes.FOREGROUND_IMAGE, step: {boyImage: 'POOL_OUT_FOREGROUND_BOY', girlImage: 'POOL_OUT_FOREGROUND_GIRL', xPosition: x(0.005), yPosition: y(0.275), scale: .99}},
                ],
                sceneSteps: [
                    {type: ProgressionStepTypes.FRIEND_SPEECH, step: {audio:{stage: MakeFriendStages.POOL, number: 2}, text: "Hey, guys! Whaddya doin’?", xPosition: x(.43), yPosition: y(.18)}},
                    {type: ProgressionStepTypes.SMOKER_SPEECH, step: {audio: {stage: MakeFriendStages.POOL, number: 1}, text: "Oh, just hangin’ out.", xPosition: x(.27), yPosition: y(.3)}},
                    {type: ProgressionStepTypes.FRIEND_SPEECH, step: {audio: {stage: MakeFriendStages.POOL, number: 3}, text: "Why don’t you come in and go swimmin’ with us?", xPosition: x(.42), yPosition: y(.15)}},
                    {type: ProgressionStepTypes.SMOKER_SPEECH, step: {audio: {stage: MakeFriendStages.POOL, number: 2}, text: "We spent all our money on vape pods and now we can’t pay to get in. Why don’t you guys come out here for awhile?", xPosition: x(.27), yPosition: y(.1)}},
                    {type: ProgressionStepTypes.FRIEND_SPEECH, step: {audio: {stage: MakeFriendStages.POOL, number: 4}, text: "I don’t think so. We’re having too much fun in the pool.", xPosition: x(.42), yPosition: y(.16)}},
                ]
            },
            {
                setupSteps: [
                    {type: ProgressionStepTypes.BACKGROUND, step: {imagePrefix: 'POOL_IN_YELLOW_'}},
                    {type: ProgressionStepTypes.CHARACTER, step: {type: CharacterType.YOU, context: CharacterContext.POOL_IN, xPosition: x(.519), yPosition: y(.564), scale: 1}},
                    {type: ProgressionStepTypes.NAME_TAG, step: {type: CharacterType.YOU, xPosition: x(.613), yPosition: y(.825)}},
                    {type: ProgressionStepTypes.CHARACTER, step: {type: CharacterType.FRIEND, context: CharacterContext.POOL_IN, xPosition: x(.009), yPosition: y(.476), scale: 1}},
                    {type: ProgressionStepTypes.NAME_TAG, step: {type: CharacterType.FRIEND, xPosition: x(.207), yPosition: y(.769)}}
                ],
                sceneSteps: [
                    {type: ProgressionStepTypes.FRIEND_SPEECH, step: {audio: {stage: MakeFriendStages.POOL, number: 5}, text: "Why would you want to vape when you can go swimming instead?", xPosition: x(.24), yPosition: y(.32), arrowLeft: true}},
                    {type: ProgressionStepTypes.MODAL, step: {choices: [{text: "Yeah, it’s much more fun swimming with you than hanging out with those vapers.", wordWrapWidth: .5}, {text: "I don’t know. I think vaping is totally boring.", wordWrapWidth: .5}, {text: "I feel bad for those guys; they’re really missing out!", wordWrapWidth: .5}]}},
                    {type: ProgressionStepTypes.YOU_SPEECH, step: {audioChoices: [{stage: MakeFriendStages.POOL, number: 4}, {stage: MakeFriendStages.POOL, number: 5}, {stage: MakeFriendStages.POOL, number: 6}], xPosition: x(.35), yPosition: y(.35)}},
                    {type: ProgressionStepTypes.FRIEND_SPEECH, step: {audio: {stage: MakeFriendStages.POOL, number: 6}, text: "Too bad those guys have to miss all the fun. Let’s see who can swim to the other end first!", xPosition: x(.24), yPosition: y(.26), arrowLeft: true}},
                    {type: ProgressionStepTypes.NARRATION, step: {audio: MakeFriendAudio.POOL_END}},
                    {type: ProgressionStepTypes.NEXT_BUTTON, step: {}},
                ]
            },
        ]
    }
]

export class MakeFriendStage {
    readonly stage: MakeFriendStages
    private progression: StageProgression
    private sceneGroup: Phaser.Group

    constructor(game: Phaser.Game, stageType: StageTypes) {
        if (stageType === StageTypes.ARCADE) {
            this.stage = MakeFriendStages.ARCADE
            this.progression = MakeFriendStageProgressions.filter((progression) => progression.type === MakeFriendStages.ARCADE)[0]
        } else if (stageType === StageTypes.SLEEPOVER) {
            this.stage = MakeFriendStages.SLEEPOVER
            this.progression = MakeFriendStageProgressions.filter((progression) => progression.type === MakeFriendStages.SLEEPOVER)[0]
        } else {
            this.stage = MakeFriendStages.POOL
            this.progression = MakeFriendStageProgressions.filter((progression) => progression.type === MakeFriendStages.POOL)[0]
        }

        const numScenes = this.progression.scenes.length

        const playScene = (sceneNumber: number) => {
            // At the end of the stage (finished last scene), complete it
            if (sceneNumber + 1 > numScenes) {
                MakeFriendProgressStore.instance.addStage(this.stage)
                game.state.start(ActivityKey.MAKE_A_FRIEND)
                return
            }
            this.sceneGroup = game.add.group()
            const nameTagGroup = game.add.group()
            const modalGroup = game.add.group()
            const buttonGroup = game.add.group()
            let bubbleGroup = game.add.group()
            const scene = this.progression.scenes[sceneNumber]
            let nextYouSpeechText = ""
            let nextYouSpeechAudioSelectedIndex = 0

            function setXAndYPositions(stepData) {
                let xPosition, yPosition
                if (stepData.xPosition != null && stepData.yPosition != null) {
                    xPosition = stepData.xPosition
                    yPosition = stepData.yPosition
                }
                else if (!MakeFriendProgressStore.isBoy()) {
                    xPosition = stepData.girlXPosition
                    yPosition = stepData.girlYPosition
                } else if (MakeFriendProgressStore.isBoy()) {
                    xPosition = stepData.boyXPosition
                    yPosition = stepData.boyYPosition
                }
                return {xPosition, yPosition}
            }

            scene.setupSteps.forEach((step) => {
                const type = step.type
                let stepData
                let image
                let positions
                switch (type) {
                    case ProgressionStepTypes.BACKGROUND:
                        stepData = step.step as ShowBackground
                        if (stepData.image) {
                            image = stepData.image
                        } else {
                            image = stepData.imagePrefix + MakeFriendProgressStore.getGender.toUpperCase()
                        }
                        const background = new BackgroundSprite(game, MakeFriendImages[image])
                        this.sceneGroup.add(background)
                        break
                    case ProgressionStepTypes.CHARACTER:
                        stepData = step.step as Character
                        let flavor
                        if (stepData.type == CharacterType.YOU) {
                            flavor = MakeFriendProgressStore.getYouCharacterFlavor
                        } else {
                            flavor = MakeFriendProgressStore.getFriendCharacterFlavor
                        }
                        positions = setXAndYPositions(stepData)
                        const charXPosition = positions.xPosition
                        const charYPosition = positions.yPosition
                        MakeFriendCharacter.add(game, stepData.type, flavor, stepData.context, {
                            x: charXPosition,
                            y: charYPosition,
                            scale: stepData.scale,
                            group: this.sceneGroup
                        })
                        break
                    case ProgressionStepTypes.FOREGROUND_IMAGE:
                        stepData = step.step as ForegroundImage
                        if (stepData.girlImage && !MakeFriendProgressStore.isBoy()) {
                            image = stepData.girlImage
                        } else if (stepData.boyImage && MakeFriendProgressStore.isBoy()) {
                            image = stepData.boyImage
                        } else {
                            image = stepData.image
                        }

                        image = Sprites.add(game, MakeFriendImages[image],
                            {x: stepData.xPosition, y: stepData.yPosition, scale: stepData.scale, group: this.sceneGroup, debug: true})
                        nameTagGroup.add(image)
                        break
                    case ProgressionStepTypes.NAME_TAG:
                        stepData = step.step as NameTag
                        let text = stepData.type == CharacterType.YOU ? StudentStoreSA.instance.findUsername() : MakeFriendProgressStore.getFriendName
                        if (!text) {
                            text = "Unknown"
                        }
                        const rectangle = new Rectangle(game, 0, 0, x(.15), y(.05), {
                            fillColor: colorNum.white, fillAlpha: .5, lineWidth: 0
                        })
                        positions = setXAndYPositions(stepData)
                        const nameXPosition = positions.xPosition
                        const nameYPosition = positions.yPosition
                        rectangle.graphics.centerX = nameXPosition
                        rectangle.graphics.centerY = nameYPosition
                        const nameTag = PhaserText.create(game, text, {
                            align: Align.CENTER, type: FontType.HEADER, group: nameTagGroup
                        })
                        Sprites.centerSpriteInSprite(rectangle.graphics, nameTag)

                        break
                    default:
                        throw new Error(`Unknown Make a Friend Progression Step Type: ${step.type}`)
                }
            })
            game.world.bringToTop(nameTagGroup)
            game.world.bringToTop(modalGroup)
            game.world.bringToTop(buttonGroup)

            const numSteps = scene.sceneSteps.length

            const performStep = (stepNumber: number) => {
                // At the end of the scene (finished last step), destroy the scene and play the next one
                bubbleGroup.destroy()
                bubbleGroup = game.add.group()
                if (stepNumber + 1 > numSteps) {
                    this.sceneGroup.destroy()
                    playScene(sceneNumber + 1)
                    return
                }
                const step = scene.sceneSteps[stepNumber]
                const type = step.type
                let stepData
                let bubble
                let audio
                let positions

                function displayBubble(stepData) {
                    let bubbleWidth
                    let text
                    let right = true

                    if (stepData.girlWidth && !MakeFriendProgressStore.isBoy()) {
                        bubbleWidth = stepData.girlWidth
                    } else if (stepData.boyWidth && MakeFriendProgressStore.isBoy()) {
                        bubbleWidth = stepData.boyWidth
                    } else {
                        bubbleWidth = stepData.width ?? .2
                    }

                    if (stepData.girlText && !MakeFriendProgressStore.isBoy()) {
                        text = stepData.girlText
                    } else if (stepData.boyText && MakeFriendProgressStore.isBoy()) {
                        text = stepData.boyText
                    } else {
                        text = stepData.text ? stepData.text : nextYouSpeechText
                    }

                    if (stepData.girlArrowLeft && !MakeFriendProgressStore.isBoy()) {
                        right = false
                    } else if (stepData.boyArrowLeft && MakeFriendProgressStore.isBoy()) {
                        right = false
                    } else if (stepData.arrowLeft) {
                        right = false
                    }

                    positions = setXAndYPositions(stepData)
                    const bubbleXPosition = positions.xPosition
                    const bubbleYPosition = positions.yPosition
                    return SpeechBubble.create(game, {
                        text: text,
                        x: bubbleXPosition,
                        y: bubbleYPosition,
                        group: bubbleGroup,
                        right: right,
                        width: bubbleWidth,
                    })
                }

                switch (type) {
                    case ProgressionStepTypes.YOU_SPEECH:
                        // Display speech bubble and play audio
                        stepData = step.step as YouSpeech
                        bubble = displayBubble(stepData)
                        const youStage = stepData.audioChoices[nextYouSpeechAudioSelectedIndex].stage
                        const youNumber = stepData.audioChoices[nextYouSpeechAudioSelectedIndex].number
                        const youAudioName = MakeFriendAssets.getStageAudio(youStage, CharacterType.YOU, MakeFriendProgressStore.getGender, youNumber, MakeFriendProgressStore.getYouCharacterFlavor)
                        audio = game.add.audio(youAudioName)
                        audio.onStop.addOnce(() => {
                            performStep(stepNumber + 1)
                        })
                        audio.play()
                        break
                    case ProgressionStepTypes.FRIEND_SPEECH:
                        // Display speech bubble and play audio
                        stepData = step.step as NPCSpeech
                        bubble = displayBubble(stepData)
                        const friendStage = stepData.audio.stage
                        const friendNumber = stepData.audio.number
                        const friendAudioName = MakeFriendAssets.getStageAudio(friendStage, CharacterType.FRIEND, MakeFriendProgressStore.getGender, friendNumber)
                        audio = game.add.audio(friendAudioName)
                        audio.onStop.addOnce(() => {
                            performStep(stepNumber + 1)
                        })
                        audio.play()
                        break
                    case ProgressionStepTypes.SMOKER_SPEECH:
                        // Display speech bubble and play audio
                        stepData = step.step as NPCSpeech
                        bubble = displayBubble(stepData)
                        const smokerStage = stepData.audio.stage
                        const smokerNumber = stepData.audio.number
                        const smokerAudioName = MakeFriendAssets.getStageAudio(smokerStage, CharacterType.SMOKER, MakeFriendProgressStore.getGender, smokerNumber)
                        audio = game.add.audio(smokerAudioName)
                        audio.onStop.addOnce(() => {
                            performStep(stepNumber + 1)
                        })
                        audio.play()
                        break
                    case ProgressionStepTypes.NARRATION:
                        // Play audio
                        stepData = step.step as Narration
                        audio = game.add.audio(stepData.audio)
                        audio.onStop.addOnce(() => {
                            performStep(stepNumber + 1)
                        })
                        audio.play()
                        break
                    case ProgressionStepTypes.MODAL:
                        // Show modal with options
                        stepData = step.step as Modal
                        const modal = new Rectangle(
                            game, x(0.15), y(0.15), x(0.65), y(0.7),
                            {
                                lineColor: colorNum.black,
                                fillColor: colorNum.white,
                                cornerRounding: 20,
                                lineWidth: 4,
                                group: modalGroup
                            }
                        )
                        const text = PhaserText.create(game, "What do you want to say to your friend?", { y: y(.2), fontPx: 18, group: modalGroup })
                        Sprites.centerTextInSprite(modal.graphics, text, true, true)
                        let choices
                        if (stepData.girlChoices && !MakeFriendProgressStore.isBoy()) {
                            choices = stepData.girlChoices
                        } else if (stepData.boyChoices && MakeFriendProgressStore.isBoy()) {
                            choices = stepData.boyChoices
                        } else {
                            choices = stepData.choices
                        }

                        const nextButton = Buttons.create(
                            game,
                            "Next",
                            () => {
                                modalGroup.destroy()
                                nextYouSpeechText = radioGroup.currentlySelected
                                nextYouSpeechAudioSelectedIndex = radioGroup.currentlySelectedIndex
                                performStep(stepNumber + 1)
                            },
                            { y: y(0.7), width: 0.15, enabled: false, group: modalGroup, buttonType: ButtonType.PRIMARY, centerInWorldX: true }
                        )

                        const radioGroup = new RadioGroup(game, Scale.x(0.22), Scale.y(0.3), choices,
                            () => nextButton.enable(), false, false, 18, modalGroup
                        )
                        audio = game.add.audio(MakeFriendAudio.PICK_WHAT_YOU_WANT_TO_SAY)
                        audio.onStop.addOnce(radioGroup.enable)
                        audio.play()
                        break
                    case ProgressionStepTypes.NEXT_BUTTON:
                        // Display a next button that does to the next step on click
                        stepData = step.step as NextButton
                        Buttons.create(
                            game,
                            "Next",
                            () => performStep(stepNumber + 1),
                            { x: x(0.8), y: y(0.9), width: 0.15, enabled: true, group: buttonGroup, buttonType: ButtonType.PRIMARY }
                        )
                        break
                    case ProgressionStepTypes.FADE_SCREEN:
                        // Fade in a black screen with optional text
                        stepData = step.step as FadeScreen
                        const fadeScreenGroup = game.add.group()
                        let initialAlpha, targetAlpha
                        if (stepData.unfade) {
                            initialAlpha = 1
                            targetAlpha = 0
                        } else {
                            initialAlpha = 0
                            targetAlpha = 1
                        }
                        const blackScreen = Sprites.add(game, MakeFriendImages.BLACK_SCREEN, {alpha: initialAlpha, x: x(0), y: y(0), group: fadeScreenGroup})
                        blackScreen.width = x(1)
                        blackScreen.height = y(1)
                        const showBlackScreenTween = game.add.tween(blackScreen).to({alpha: targetAlpha}, stepData.duration, Phaser.Easing.Linear.None)
                        showBlackScreenTween.onComplete.addOnce(() => performStep(stepNumber + 1))
                        showBlackScreenTween.start()
                        if (stepData.text) {
                            const text = PhaserText.create(game, stepData.text, {color: COLOR.white, alpha: initialAlpha, group: fadeScreenGroup})
                            Sprites.centerTextInSprite(blackScreen, text)
                            const showTextTween = game.add.tween(text).to({alpha: targetAlpha}, stepData.duration, Phaser.Easing.Linear.None)
                            showTextTween.start()
                        }
                        break
                    default:
                        throw new Error(`Unknown Make a Friend Progression Step Type: ${step.type}`)
                }
            }
            performStep(0)
        }
        playScene(0)
    }
}